import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Button } from '../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Pencil, Archive, Play, Pause, Copy, Workflow as WorkflowIcon } from 'lucide-react';
import ConfirmDialog from '../ConfirmDialog';
import CreateWorkflowDialog from './CreateWorkflowDialog';
import { useAuth } from '../../context/authContext';
import { useToast } from '../ui/use-toast';
import { createWorkflow, updateWorkflow, archiveWorkflow } from '../../services/workflow.service';

const workflowStatusStyles = {
    ACTIVE: { label: 'Active', text: 'text-emerald-700', dot: 'bg-yavrio-green' },
    DRAFT: { label: 'Draft', text: 'text-amber-700', dot: 'bg-amber-500' },
    INACTIVE: { label: 'Inactive', text: 'text-slate-500', dot: 'bg-slate-400' },
    ARCHIVED: { label: 'Archived', text: 'text-red-700', dot: 'bg-red-500' },
};

const WorkflowStatus = ({ status }) => {
    const styles = workflowStatusStyles[status] || {
        label: status,
        text: 'text-slate-600',
        dot: 'bg-slate-400',
    };
    return (
        <div className="flex items-center gap-2">
            <span className={`h-2 w-2 rounded-full shrink-0 ${styles.dot}`} />
            <span className={`text-sm font-medium ${styles.text}`}>{styles.label}</span>
        </div>
    );
};

export default function WorkflowsList({ workflows, onChanged }) {

    const { user } = useAuth();
    const { toast } = useToast();
    const navigate = useNavigate();

    const handleCreate = async (name) => {
        const response = await createWorkflow(user.token, user.organisationId, { name });
        if (response && response.status === 201) {
            toast({ title: 'Success', description: 'Workflow created', variant: 'success' });
            onChanged && onChanged();
            navigate(`/workflows/${response.data.data.workflowId}`);
            return true;
        }
        const message = response?.data?.error?.message || 'There was an error creating the workflow';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        return false;
    };

    const handleStatusChange = async (workflowId, status) => {
        const response = await updateWorkflow(user.token, user.organisationId, workflowId, { status });
        if (response && response.status === 200) {
            toast({ title: 'Success', description: `Workflow set to ${status}`, variant: 'success' });
            onChanged && onChanged();
        } else {
            const message = response?.data?.error?.message || 'There was an error updating the workflow';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    const handleArchive = async (workflowId) => {
        const response = await archiveWorkflow(user.token, user.organisationId, workflowId);
        if (response && (response.status === 200 || response.status === 204)) {
            toast({ title: 'Success', description: 'Workflow archived', variant: 'success' });
            onChanged && onChanged();
        } else {
            const message = response?.data?.error?.message || 'There was an error archiving the workflow';
            toast({ title: 'Error', description: message, variant: 'destructive' });
        }
    };

    const copyId = (workflowId) => {
        navigator.clipboard?.writeText(workflowId);
        toast({ title: 'Copied', description: 'Workflow ID copied to clipboard', variant: 'success' });
    };

    // Single-workflow mode: an organisation may only have one non-archived custom
    // workflow. Hide the create action once one exists.
    const hasActiveWorkflow = workflows.some((w) => w.status !== 'ARCHIVED');

    return (
        <Card>
            <CardHeader>
                <div className="flex flex-row items-center justify-between">
                    <div className="flex items-center gap-2">
                        <WorkflowIcon className="w-5 h-5 text-yavrio-blue" />
                        <h3 className="my-0">Workflows</h3>
                    </div>
                    {!hasActiveWorkflow && (
                        <CreateWorkflowDialog onCreate={handleCreate}>
                            <Button className="yvr-button gap-2">
                                <Plus className="w-4 h-4" /> New Workflow
                            </Button>
                        </CreateWorkflowDialog>
                    )}
                </div>
            </CardHeader>
            <CardContent>
                {workflows.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        No workflows yet. Create a workflow to define value-tiered approval steps for incoming
                        payments.
                    </p>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="hover:bg-white">
                                <TableHead>Name</TableHead>
                                <TableHead>Workflow ID</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {workflows.map((workflow) => (
                                <TableRow key={workflow.id || workflow.workflowId} className="hover:bg-white">
                                    <TableCell className="font-medium">{workflow.name}</TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <code className="text-xs bg-slate-100 rounded px-2 py-1">{workflow.workflowId}</code>
                                            <button
                                                type="button"
                                                onClick={() => copyId(workflow.workflowId)}
                                                className="text-gray-400 hover:text-gray-600"
                                                title="Copy workflow ID"
                                            >
                                                <Copy className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <WorkflowStatus status={workflow.status} />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center justify-end gap-1">
                                            {workflow.status !== 'ARCHIVED' && (
                                                <Button
                                                    variant="ghost"
                                                    className="p-2 text-yavrio-blue"
                                                    title="Edit"
                                                    onClick={() => navigate(`/workflows/${workflow.workflowId}`)}
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                            )}
                                            {workflow.status === 'ACTIVE' && (
                                                <ConfirmDialog
                                                    title="Deactivate Workflow"
                                                    description="Incoming payments will no longer be able to use this workflow. Continue?"
                                                    cancel="Cancel"
                                                    action="Deactivate"
                                                    handleAction={() => handleStatusChange(workflow.workflowId, 'INACTIVE')}
                                                >
                                                    <Button variant="ghost" className="p-2 text-amber-600" title="Deactivate">
                                                        <Pause className="h-4 w-4" />
                                                    </Button>
                                                </ConfirmDialog>
                                            )}
                                            {workflow.status === 'INACTIVE' && (
                                                <ConfirmDialog
                                                    title="Activate Workflow"
                                                    description="Make this workflow available to incoming payments again?"
                                                    cancel="Cancel"
                                                    action="Activate"
                                                    handleAction={() => handleStatusChange(workflow.workflowId, 'ACTIVE')}
                                                >
                                                    <Button variant="ghost" className="p-2 text-yavrio-green" title="Activate">
                                                        <Play className="h-4 w-4" />
                                                    </Button>
                                                </ConfirmDialog>
                                            )}
                                            {workflow.status !== 'ARCHIVED' && (
                                                <ConfirmDialog
                                                    title="Archive Workflow"
                                                    description={
                                                        <>
                                                            {`Are you sure you want to archive `}
                                                            <b>{workflow.name}</b>
                                                            {`? This cannot be undone and the workflow will no longer be usable.`}
                                                        </>
                                                    }
                                                    cancel="Cancel"
                                                    action="Archive"
                                                    variant="destructive"
                                                    handleAction={() => handleArchive(workflow.workflowId)}
                                                >
                                                    <Button variant="ghost" className="p-2 text-red-400" title="Archive">
                                                        <Archive className="h-4 w-4" />
                                                    </Button>
                                                </ConfirmDialog>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
                {hasActiveWorkflow && (
                    <p className="text-xs text-slate-400 mt-4 mb-0">
                        Only one custom workflow is allowed per organisation. Archive the existing workflow to
                        create a new one.
                    </p>
                )}
            </CardContent>
        </Card>
    );
}
